import { useEffect, useState } from "react";
import { Landmark, Loader2, Save, Upload, RefreshCw, Unlink, Copy, CheckCircle2, AlertTriangle, Info, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { api, errMsg } from "../lib/api";
import { fmtDate, fmtDateTime, fmtEUR } from "../lib/format";
import { BankPicker } from "./BankPicker";

const Card = ({ title, children, testId }) => (
  <section className="grid gap-4 rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
    <h2 className="font-display text-base font-bold text-slate-900 md:text-lg">{title}</h2>
    {children}
  </section>
);

const Steps = ({ redirect }) => (
  <ol className="list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-slate-600" data-testid="bank-setup-steps">
    <li>Auf <a href="https://enablebanking.com" target="_blank" rel="noreferrer" className="text-blue-700 underline">enablebanking.com</a> registrieren und das Control Panel öffnen.</li>
    <li>Unter „API applications“ eine Anwendung im Modus <b>Production</b> anlegen und als Redirect-URL eintragen: <code className="break-all">{redirect || "(zuerst öffentliche Adresse unter „Agent“ eintragen)"}</code></li>
    <li>Beim Anlegen den Schlüssel erzeugen lassen und die <b>.pem</b>-Datei herunterladen.</li>
    <li>Auf <b>„Activate by linking accounts“</b> klicken und dein Erste-Konto (George) verknüpfen – damit ist die Anwendung für deine eigenen Konten kostenlos freigeschaltet.</li>
    <li>Hier die <b>Application ID</b> einfügen und die <b>.pem</b> hochladen, dann „Mit George verbinden“.</li>
  </ol>
);

const Redirect = ({ url }) => (
  <div className="grid gap-1.5">
    <Label className="text-sm font-semibold text-slate-700">Redirect-URL (in die Enable-Banking-Anwendung kopieren)</Label>
    <div className="flex gap-2">
      <Input readOnly value={url || "Bitte zuerst unter „Agent“ die öffentliche Adresse der App eintragen"} className="h-10 font-mono text-xs" data-testid="bank-redirect-url" />
      <Button variant="outline" disabled={!url} onClick={() => { navigator.clipboard?.writeText(url); toast.success("Kopiert"); }} className="h-10" data-testid="bank-redirect-copy-btn"><Copy className="h-4 w-4" /></Button>
    </div>
  </div>
);

const CredCard = ({ s, setS }) => {
  const [appId, setAppId] = useState(s.app_id || "");
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try { setS(await api.bankUploadKey(f)); toast.success("Schlüssel verschlüsselt gespeichert"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Enable Banking – Zugang" testId="bank-cred-card">
      <Steps redirect={s.redirect_url} />
      <Redirect url={s.redirect_url} />
      <div className="flex flex-wrap items-end gap-2">
        <label className="grid flex-1 gap-1.5 text-sm font-semibold text-slate-700">Application ID
          <Input value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="z. B. 3f2a…-…" className="h-10 font-mono" data-testid="bank-app-id-input" />
        </label>
        <Button onClick={async () => { try { setS(await api.bankSaveSettings({ app_id: appId })); toast.success("Gespeichert"); } catch (e) { toast.error(errMsg(e)); } }} className="h-10 gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid="bank-app-id-save-btn"><Save className="h-4 w-4" />Speichern</Button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" asChild className="h-10 gap-1.5" data-testid="bank-key-upload-btn">
          <label className="cursor-pointer">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Privaten Schlüssel (.pem) hochladen
            <input type="file" accept=".pem,.key,application/x-pem-file" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="bank-key-file-input" />
          </label>
        </Button>
        <span className="text-sm text-slate-600" data-testid="bank-key-status">{s.mock ? "Simulation aktiv" : s.key_uploaded ? "✓ Schlüssel gespeichert (verschlüsselt, wird nie angezeigt)" : "Noch kein Schlüssel"}</span>
      </div>
    </Card>
  );
};

const RUN_TXT = { ok: "✓", error: "Fehler", skipped: "übersprungen", ausstehend: "ausstehend", "nicht gelaufen": "nicht gelaufen" };
const AutoRuns = ({ runs }) => !runs?.length ? null : (
  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500" data-testid="bank-auto-runs">Heute automatisch:
    {runs.map((r) => (
      <span key={r.time} data-testid={`bank-auto-run-${r.time.replace(":", "")}`} title={r.error || r.reason || ""}
        className={r.status === "ok" ? "text-emerald-700" : r.status === "error" ? "text-red-700" : ""}>
        <b className="font-num">{r.time}</b> {RUN_TXT[r.status] || r.status}{r.status === "ok" ? ` ${r.new} neu` : ""}{r.reason ? ` (${r.reason})` : ""}
      </span>
    ))}
  </div>
);

const dm = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.` : "…");
const LastResult = ({ r }) => !r ? null : r.ok ? (
  <span data-testid="bank-last-result"> ({r.trigger === "auto" ? "automatisch" : "manuell"}) — <b className="text-slate-700">{r.new} neu</b>, {r.existing} bereits vorhanden, {r.pending} vorgemerkt{r.skipped ? `, ${r.skipped} unlesbar übersprungen` : ""} (Zeitraum {dm(r.date_from)}–{dm(r.date_to)}){r.note && <span className="text-amber-700" data-testid="bank-window-note"> · {r.note}</span>}</span>
) : (
  <span data-testid="bank-last-result" className="text-red-700"> — Fehler{r.step ? ` bei „${r.step}“` : ""}{r.http_status ? ` (HTTP ${r.http_status})` : ""}: {r.error}</span>
);


const ConnCard = ({ s, setS, reload }) => {
  const [busy, setBusy] = useState("");
  const run = async (k, fn) => { setBusy(k); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  const connect = () => run("c", async () => { const r = await api.bankConnect(window.location.origin); window.location.assign(r.url); });
  return (
    <Card title="Bankverbindung" testId="bank-conn-card">
      {s.configured && <BankPicker s={s} onChange={async (v) => setS(await api.bankSaveSettings(v))} />}
      <div className="flex flex-wrap items-end gap-3">
        <Button disabled={!s.configured || !s.aspsp_name || !!busy} onClick={connect} className="h-10 gap-1.5 bg-slate-900 hover:bg-slate-800" data-testid="bank-connect-btn">
          {busy === "c" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}{s.connected ? "Verbindung erneuern" : "Mit George verbinden"}
        </Button>
      </div>
      {s.connected && (
        <div className="grid gap-2 text-sm text-slate-700" data-testid="bank-status">
          <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" />Konto <b className="font-num">{s.iban}</b> {s.account_name && `(${s.account_name})`}</div>
          {!s.iban_matched && <div className="flex flex-wrap items-center gap-2 text-amber-800" data-testid="bank-iban-warning">Achtung: Diese IBAN passt zu keinem hochgeladenen Kontoauszug.
            {s.accounts?.length > 1 && <select value="" onChange={async (e) => e.target.value && setS(await api.bankChooseAccount(e.target.value))} className="h-8 rounded-md border border-input bg-white px-2 text-xs" data-testid="bank-account-select">
              <option value="">Anderes Konto wählen…</option>
              {s.accounts.map((a) => <option key={a.uid} value={a.uid}>{a.name} · {a.iban}</option>)}
            </select>}
          </div>}
          <div data-testid="bank-valid-until">Freigabe gültig bis <b>{fmtDate(s.valid_until?.slice(0, 10))}</b>{s.days_left != null && ` (noch ${s.days_left} Tage)`}{s.days_left != null && s.days_left <= 7 && <span className="ml-2 rounded bg-amber-100 px-1.5 text-amber-900">bald erneuern</span>}</div>
          {s.balance && <div data-testid="bank-balance">Aktueller Kontostand: <b className="font-num">{fmtEUR(s.balance.amount)}</b> <span className="text-xs text-slate-500">({s.balance.name || s.balance.type}{s.balance.reference_date ? `, ${fmtDate(s.balance.reference_date)}` : ""})</span></div>}
          <div className="text-xs text-slate-500" data-testid="bank-sync-status">Letzter Abruf: {fmtDateTime(s.last_sync)}<LastResult r={s.last_result} /> · Nächster automatischer Abruf: <span data-testid="bank-next-sync">{s.next_sync_label}</span> · Heute {s.fetches_today}/{s.max_auto_per_day} automatische Abrufe</div>
          <AutoRuns runs={s.auto_today} />
        </div>
      )}
      {s.sync_error && <p className="flex items-start gap-1.5 rounded-lg bg-red-50 p-3 text-sm text-red-800" data-testid="bank-error"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{s.sync_error}</p>}
      {s.connected && (
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" disabled={!!busy} onClick={() => run("s", async () => { const r = await api.bankSync(); toast.success(r.skipped ? "Abruf läuft bereits" : `${r.new} neu, ${r.existing} bereits vorhanden, ${r.pending} vorgemerkt`); await reload(); })} className="h-10 gap-1.5" data-testid="bank-sync-btn">
            {busy === "s" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}Jetzt abrufen
          </Button>
          <label className="flex items-center gap-2 text-sm"><Switch checked={s.sync_enabled} onCheckedChange={async (v) => setS(await api.bankSaveSettings({ sync_enabled: v }))} data-testid="bank-auto-switch" />automatisch</label>
          <span className="text-sm text-slate-600" data-testid="bank-slots-text">Automatisch um {(s.auto_slots || []).join(", ")}</span>
          <Button variant="ghost" onClick={() => run("d", async () => { setS(await api.bankDisconnect()); toast.success("Bankverbindung getrennt"); })} className="h-10 gap-1.5 text-red-700 hover:bg-red-50" data-testid="bank-disconnect-btn"><Unlink className="h-4 w-4" />Trennen</Button>
        </div>
      )}
    </Card>
  );
};

const Pending = ({ items }) => !items?.length ? null : (
  <Card title="Vorgemerkt (noch nicht gebucht)" testId="bank-pending">
    <ul className="grid gap-1 text-sm text-slate-400">
      {items.map((p, i) => <li key={i} className="flex justify-between gap-3"><span>{fmtDate(p.booking_date)} · {p.counterparty_raw || p.booking_text}</span><span className="font-num">{fmtEUR(p.amount)}</span></li>)}
    </ul>
    <p className="text-xs text-slate-500">Vorgemerkte Umsätze werden nicht importiert und nicht gezählt.</p>
  </Card>
);

export default function BankingSettings() {
  const [s, setS] = useState(null);
  const reload = () => api.bankSettings().then(setS);
  useEffect(() => {
    reload();
    const p = new URLSearchParams(window.location.search);
    if (p.get("bank")) {
      (p.get("bank") === "ok" ? toast.success : toast.error)(p.get("msg") || "");
      window.history.replaceState(null, "", window.location.pathname);
      setTimeout(reload, 4000);
    }
  }, []);
  if (!s) return null;
  return (
    <div className="grid max-w-3xl gap-5 p-4 sm:p-6 lg:p-8 fade-up" data-testid="banking-view">
      <div>
        <div className="text-sm text-slate-500">Einstellungen</div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"><Landmark className="h-6 w-6" />Bankverbindung</h1>
      </div>
      <div className="flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950"><Info className="mt-0.5 h-4 w-4 shrink-0" />
        Neue Buchungen kommen automatisch über Enable Banking (PSD2, nur lesend). Bis der Kontoauszug als PDF kommt, liegen sie unter „Laufende Buchungen“ – beim Hochladen des PDFs werden sie zusammengeführt, Belege und Status bleiben erhalten.</div>
      <CredCard key={s.app_id} s={s} setS={setS} />
      <ConnCard s={s} setS={setS} reload={reload} />
      <Pending items={s.pending} />
    </div>
  );
}
